import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { Grid, Typography, useMediaQuery } from '@rbx/ui';
import Article from '@modules/article/components/Article';
import { MarkdownField } from '@modules/utilities/types';
import AppLayout from '@modules/layout/layout/components/AppLayout';
import AppIAHeading from '@modules/layout/layout/components/AppIAHeading';
import AppRightNav from '@modules/layout/layout/components/AppRightNav';
import AppContent from '@modules/layout/layout/components/AppContent';
import MobileMilestoneNavigation from '@modules/learning/components/MobileCourseProgressBar';
import { TestId } from '@modules/utilities/constants/testConstants';
import Heading from '@modules/utilities/interfaces/Heading';
import { ArticleNavigationSectionProps } from '@modules/article/components/ArticleNavigationSection';
import { useLessonBreadcrumbs } from '@modules/learning/utils/lessonBreadcrumbsUtils';
import LanguageMenu from '@modules/localization/components/LanguageMenu';
import CourseProgressBar from '@modules/learning/components/CourseProgressBar';
import LessonNavigationSection from '@modules/learning/components/LessonNavigationSection';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import PageBreadcrumbs from '@modules/ui/shared/components/PageBreadcrumbs';
import useCoursesStyles from '@modules/learning/components/Courses.styles';
import { useRobloxAuthentication } from '@rbx/auth';
import LoginRequiredDialog from '@modules/assistant/components/LoginRequiredDialog';
import PageNotFound from '@modules/miscellaneous/common/components/error/PageNotFound';
import { useNavigationConfigs } from '@rbx/creator-hub-navigation';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsCoursesEvent from '@modules/eventStream/enum/DocsCoursesEvent';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';

export interface LessonContainerProps extends ArticleNavigationSectionProps, Heading {
  title: string;
  description: string | null;
  content: string | MarkdownField;
  slugs?: string[];
}

const LessonContainer: FunctionComponent<React.PropsWithChildren<LessonContainerProps>> = ({
  title,
  description,
  content,
  next,
  headings,
  slugs,
}) => {
  const { user: authenticatedUser, isFetched: isAuthLoaded } = useRobloxAuthentication();
  const { targetLocale } = useCurrentLocale();

  const userId = useMemo(() => {
    if (!isAuthLoaded || !authenticatedUser?.id) {
      return null;
    }
    return authenticatedUser.id.toString();
  }, [isAuthLoaded, authenticatedUser]);

  const breadcrumbsItems = useLessonBreadcrumbs(slugs || []);
  const { enableNewLayout, enableCourses, isIXPFetched } = useAppConfig();
  const currentLessonPath = slugs && slugs.length >= 4 ? `/${slugs.join('/')}` : undefined;
  const { classes } = useCoursesStyles();
  const { toggleProductNavigationDrawer, productNavigationDrawerOpen } = useNavigationConfigs();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('Large'));
  useEffect(() => {
    if (enableNewLayout) {
      toggleProductNavigationDrawer(false);
    }
  }, [enableNewLayout, toggleProductNavigationDrawer]);

  useEffect(() => {
    if (isAuthLoaded && userId && currentLessonPath) {
      const courseId = slugs && slugs.length >= 3 ? slugs[2] : '';
      const milestoneId = slugs && slugs.length >= 4 ? slugs[3] : '';
      const lessonId = slugs && slugs.length >= 5 ? slugs[4] : '';

      unifiedLoggerClient.logImpressionEvent({
        eventName: DocsCoursesEvent.openLessonPage,
        parameters: {
          locale: targetLocale ? String(targetLocale) : '',
          courseId,
          milestoneId,
          lessonId,
          lessonTitle: title,
          lessonPath: currentLessonPath,
          enableNewLayout: String(enableNewLayout),
        },
      });
    }
  }, [isAuthLoaded, userId, currentLessonPath, slugs, title, targetLocale, enableNewLayout]);

  useEffect(() => {
    if (isAuthLoaded && !userId) {
      unifiedLoggerClient.logImpressionEvent({
        eventName: DocsCoursesEvent.openLoginRequiredDialog,
        parameters: {
          locale: targetLocale ? String(targetLocale) : '',
          lessonTitle: title,
        },
      });
    }
  }, [isAuthLoaded, userId, targetLocale, title]);

  // TODO: Remove this once we complete AB tests
  useEffect(() => {
    const shouldShowNotFound =
      process.env.enableCourses !== true && isIXPFetched && enableCourses !== true;

    if (shouldShowNotFound) {
      unifiedLoggerClient.logImpressionEvent({
        eventName: DocsCoursesEvent.openNotFoundPage,
        parameters: {
          locale: targetLocale ? String(targetLocale) : '',
          sourcePage: 'lesson_page',
          requestedPath: currentLessonPath || '',
        },
      });
    }
  }, [isIXPFetched, enableCourses, targetLocale, currentLessonPath]);

  const lessonHeadings =
    headings && headings.length > 0
      ? headings
      : [{ id: 'lesson-content', level: 2, slug: 'lesson-content', title: 'Lesson Content' }];

  if (process.env.enableCourses !== true && isIXPFetched && enableCourses !== true) {
    return (
      <AppLayout
        testid={TestId.LessonContainer}
        pageMetaDescription='The requested lesson could not be found'
        headings={[]}
        openGraphMeta={{}}>
        <AppContent>
          <PageNotFound />
        </AppContent>
      </AppLayout>
    );
  }

  if (!isAuthLoaded) {
    return null;
  }

  if (!userId) {
    return <LoginRequiredDialog />;
  }

  return (
    <AppLayout
      pageTitle={title}
      testid={TestId.LessonContainer}
      pageMetaDescription={description}
      headings={lessonHeadings}
      openGraphMeta={{}}>
      {enableNewLayout && <AppIAHeading breadcrumbsItems={breadcrumbsItems} />}
      <AppContent>
        <div className={classes.lessonContentContainer}>
          <Grid container paddingBottom='24px'>
            {enableNewLayout && (
              <Grid container justifyContent='space-between'>
                <Typography variant='h1'>{title}</Typography>
                <LanguageMenu />
              </Grid>
            )}
            {!enableNewLayout && (
              <Grid container direction='column'>
                <Grid container className={classes.oldLayoutLessonTitle}>
                  <PageBreadcrumbs items={breadcrumbsItems} />
                  <LanguageMenu />
                </Grid>
                <Typography variant='h1'>{title}</Typography>
              </Grid>
            )}
          </Grid>
          <Article content={content} />
          <LessonNavigationSection next={next} currentLessonPath={currentLessonPath} />
        </div>
        {isMobile && <MobileMilestoneNavigation />}
      </AppContent>
      {!isMobile && <AppRightNav content={<CourseProgressBar />} />}
    </AppLayout>
  );
};

export default LessonContainer;
